import { Component } from '@theme/component';

const SHAPE_MS = 80;
const COLLAPSE_MS = 280;

/**
 * FAQ section with a question list and a single active content panel.
 *
 * @typedef {Object} FaqRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} [panels]
 * @property {HTMLDetailsElement[]} [items]
 * @property {HTMLElement} [body]
 *
 * @extends {Component<FaqRefs>}
 */
class FaqComponent extends Component {
  requiredRefs = ['tabs'];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {Map<HTMLDetailsElement, number>} */
  #shapeTimeouts = new Map();

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#activeIndex = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#abort?.abort();
    this.#abort = new AbortController();
    this.addEventListener('click', this.#handleSummaryClick, { capture: true, signal: this.#abort.signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#abort.signal });
    window.addEventListener('resize', this.#handleResize, { signal: this.#abort.signal });

    this.#sync(this.#activeIndex, { animate: false });

    if (!this.refs.panels?.length) {
      queueMicrotask(() => this.#sync(this.#activeIndex, { animate: false }));
    }

    this.#observePanels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();

    for (const timeoutId of this.#shapeTimeouts.values()) {
      window.clearTimeout(timeoutId);
    }

    this.#shapeTimeouts.clear();
  }

  /**
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();

    const nextIndex = Number(index);
    if (Number.isNaN(nextIndex) || !this.refs.tabs?.[nextIndex]) return;

    this.#sync(nextIndex, { animate: true });
  }

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #sync(index, options = {}) {
    const { animate = true } = options;
    const { tabs, panels, items } = this.refs;

    if (!tabs?.length) return;

    this.#activeIndex = index;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      tab.classList.toggle('is-active', selected);
    }

    if (items?.length) {
      for (const [i, item] of items.entries()) {
        if (!(item instanceof HTMLDetailsElement)) continue;
        this.#setItemOpen(item, i === index, animate);
      }
    }

    if (panels?.length) {
      for (const [i, panel] of panels.entries()) {
        const selected = i === index;
        panel.classList.toggle('is-active', selected);
        panel.toggleAttribute('inert', !selected);
        panel.setAttribute('aria-hidden', String(!selected));

        if (!animate) {
          panel.classList.toggle('is-visible', selected);
        }
      }

      if (animate) {
        this.#animatePanels(index);
      }
    }

    this.#updateBodyHeight();
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {boolean} open
   * @param {boolean} animate
   */
  #setItemOpen(details, open, animate) {
    const existingTimeout = this.#shapeTimeouts.get(details);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
      this.#shapeTimeouts.delete(details);
    }

    const reduceMotion = !animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (open) {
      details.classList.add('is-shaped');

      if (reduceMotion || details.open) {
        details.open = true;
        return;
      }

      const timeoutId = window.setTimeout(() => {
        details.open = true;
        this.#shapeTimeouts.delete(details);
        this.#updateBodyHeight();
      }, SHAPE_MS);

      this.#shapeTimeouts.set(details, timeoutId);
      return;
    }

    details.open = false;

    if (reduceMotion) {
      details.classList.remove('is-shaped');
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (!details.open) details.classList.remove('is-shaped');
      this.#shapeTimeouts.delete(details);
    }, COLLAPSE_MS);

    this.#shapeTimeouts.set(details, timeoutId);
  }

  /**
   * @param {number} index
   */
  #animatePanels(index) {
    const { panels } = this.refs;
    if (!panels?.length) return;

    for (const [i, panel] of panels.entries()) {
      panel.classList.toggle('is-visible', i === index);
    }
  }

  #observePanels() {
    this.#resizeObserver?.disconnect();

    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => this.#updateBodyHeight());

    for (const panel of this.refs.panels || []) {
      this.#resizeObserver.observe(panel);
    }

    for (const item of this.refs.items || []) {
      this.#resizeObserver.observe(item);
    }

    const list = this.querySelector('.faq__list');
    if (list) this.#resizeObserver.observe(list);
  }

  #updateBodyHeight() {
    const { body, panels } = this.refs;
    if (!(body instanceof HTMLElement)) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      body.style.minHeight = '';
      return;
    }

    const activePanel = panels?.[this.#activeIndex];
    const list = this.querySelector('.faq__list');
    const panelHeight = activePanel instanceof HTMLElement ? activePanel.offsetHeight : 0;
    const listHeight = list instanceof HTMLElement ? list.offsetHeight : 0;

    body.style.minHeight = `${Math.max(panelHeight, listHeight)}px`;
  }

  #handleResize = () => {
    this.#updateBodyHeight();
  };

  /**
   * Keep native <details> from toggling so only one FAQ row stays open.
   *
   * @param {MouseEvent} event
   */
  #handleSummaryClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('.faq__question')) return;

    event.preventDefault();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest?.('.faq__question')) return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const currentIndex = tabs.findIndex((tab) => tab === target || tab.contains(target));
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[nextIndex]?.focus();
    this.select(nextIndex);
  };
}

if (!customElements.get('faq-component')) {
  customElements.define('faq-component', FaqComponent);
}
